import React, { useEffect, useRef, useState } from "react";
import TopImageLayers from "../../../../../UI/TopImageLayers/TopImageLayers";
import "./TarifsCardsMobile.scss";
import TarifIcon from "../../../../../UI/TarifIcon/TarifIcon";
import { tarifBadgeText } from "../../tarifBadge";

/*
  TarifsCardsMobile
  ─────────────────
  Единый мобильный блок тарифов. Принципиально НЕ карусель:
  карточка одна и смонтирована один раз, по индексу подменяются
  только привязанные значения. Никакого .track / translateX.

  Навигация: свайп по карточке + кликабельные точки снизу
  (+ стрелки ←/→ с клавиатуры). Верхнего свитча нет — название
  тарифа видно в самой карточке.

  Подсветка: при переключении моргают ТОЛЬКО значения, у которых
  реально изменился текст (скорость/соц.сети/ночь/цена). Общие фичи
  не дёргаются. На загрузке вспышки нет. Работает только в режиме
  "variants" (Домашний/Годовые). В "gallery" (IP TV/Доп) — без вспышки.

  Фото (top_img / img) — в шапке карточки, смена анимируется кроссфейдом.

  onConnect(rawTariff) открывает ту же модалку, что десктопная карточка
  этой категории (ветвление — в TarifsCards.jsx).
*/

const SWIPE_THRESHOLD = 40;
const FLASH_MS = 650;

/* ───────── нормализация ───────── */

function formatNum(n) {
  const v = Number(n);
  return Number.isFinite(v) ? String(v) : String(n);
}

/*
  Текст бейджа — поле stock, с запасным «Новый» для Тамошо Pro и Pro Max.
  Правила общие с десктопной карточкой, лежат в tarifBadge.js. Пусто —
  бейджа нет.
*/
function computeBadge(raw) {
  const text = tarifBadgeText(raw);
  if (!text) return null;
  if (raw.link) {
    return { text, href: "https://alifshop.tj", kind: "alif" };
  }
  return { text, kind: "new" };
}

// Категории 0/1: TarifCard (API). Уверенная схема.
export function normalizeTariff(raw, activeTitle) {
  const suffix = raw.term_months === 1 ? "/мес" : ` /${raw.term_months} мес`;
  const features = (raw.tarif_infos || [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((f) => ({
      // ключ сопоставления одной и той же фичи между тарифами.
      // У годовых (period=year) API отдаёт category:"other" для ВСЕХ фич —
      // как ключ она бесполезна и даёт дубли React-key, поэтому "other"
      // игнорируем и падаем на icon → text.
      key: (f.category && f.category !== "other" ? f.category : f.icon || f.text),
      icon: f.icon || null,
      image: f.image || null,
      text: f.text,
    }));
  return {
    id: raw.id,
    name: raw.name,
    subtitle: raw.title || "",
    badge: computeBadge(raw),
    topImg: raw.top_img || null,
    // нет поля или null (в том числе из старого nets_tarifs_cache) — слоя не будет
    topImgDark: raw.top_img_dark || null,
    features,

    priceText: `${formatNum(raw.pref_price)} TJS${suffix}`,
    oldPriceText: raw.curr_price ? `${formatNum(raw.curr_price)} TJS` : null,
    raw,
  };
}

// Категории 2/3: TarifCustomCard / RouterCard (хардкод, цена строкой).
// Поля сверены с HomeModule.jsx: data/dataText/lan/longText/img.
export function normalizeGalleryItem(raw) {
  const lines = [raw.data, raw.dataText, raw.lan, raw.longText].filter(Boolean);
  return {
    id: raw.id,
    name: raw.title || raw.name,
    subtitle: raw.subTitle || "",
    badge: null,
    topImg: raw.img || null,
    topImgDark: raw.img_dark || null,
    features: lines.map((t, i) => ({ key: `g${i}`, icon: null, image: null, text: t })),
    priceText: String(raw.price || "").replace(/\s+/g, " ").trim(),
    oldPriceText: null,
    raw,
  };
}

/* ───────── части UI ───────── */

function FeatureRow({ f, flash }) {
  return (
    <li className="mtariffs__feature">
      {f.icon ? (
        <TarifIcon name={f.icon} className="mtariffs__feature-icon" />
      ) : f.image ? (
        <img className="mtariffs__feature-img" src={f.image} alt="" />
      ) : null}
      <span className={"mtariffs__feature-text" + (flash ? " is-flash" : "")}>{f.text}</span>
    </li>
  );
}

/* ───────── основной компонент ───────── */

export default function TarifsCardsMobile({ tariffs = [], activeTitle, onConnect }) {
  const [index, setIndex] = useState(0);
  const [flashKeys, setFlashKeys] = useState(() => new Set());
  const interacted = useRef(false);
  const prevIndex = useRef(0);
  const startX = useRef(null);
  // Палец тянет ленту за собой, поэтому смещение держим в состоянии
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);

  const isVariants = (activeTitle === 0 || activeTitle === 1) && tariffs.length >= 2;


  // смена категории → сброс
  useEffect(() => {
    interacted.current = false;
    prevIndex.current = 0;
    setIndex(0);
    setFlashKeys(new Set());
  }, [activeTitle]);

  // вычисляем, что изменилось, и моргаем только этим (только variants, не на загрузке)
  useEffect(() => {
    if (!isVariants || !interacted.current) {
      prevIndex.current = index;
      return;
    }
    const prev = tariffs[prevIndex.current];
    const curr = tariffs[index];
    prevIndex.current = index;
    if (!prev || !curr) return;
    const changed = new Set();
    curr.features.forEach((f) => {
      const p = prev.features.find((x) => x.key === f.key);
      if (!p || p.text !== f.text) changed.add("f:" + f.key);
    });
    if (prev.priceText !== curr.priceText) changed.add("price");
    setFlashKeys(changed);
    const t = setTimeout(() => setFlashKeys(new Set()), FLASH_MS);
    return () => clearTimeout(t);
  }, [index, isVariants, tariffs]);

  if (!tariffs.length) return null;

  const go = (i) => {
    if (i < 0 || i > tariffs.length - 1 || i === index) return;
    interacted.current = true;
    setIndex(i);
  };

  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setDragging(true);
  };
  const onTouchMove = (e) => {
    if (startX.current == null) return;
    let dx = e.touches[0].clientX - startX.current;
    // За крайними карточками лента тянется с сопротивлением
    const atEdge = (index === 0 && dx > 0) || (index === tariffs.length - 1 && dx < 0);
    if (atEdge) dx *= 0.3;
    setDrag(dx);
  };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx < -SWIPE_THRESHOLD) go(index + 1);
    else if (dx > SWIPE_THRESHOLD) go(index - 1);
    startX.current = null;
    setDragging(false);
    setDrag(0);
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") go(index + 1);
    if (e.key === "ArrowLeft") go(index - 1);
  };


  // Карточки лежат в ленте все сразу и едут трансформом — иначе смена
  // выглядит подменой содержимого, а не листанием.
  const renderCard = (active, cardIndex) => {
    const isActive = cardIndex === index;
    const flashOf = (key) => isActive && flashKeys.has(key);
    return (
      <article className="mtariffs__card" key={active.id ?? cardIndex}>
        {active.topImg ? (
          <TopImageLayers
            className="mtariffs__media"
            imgClassName="mtariffs__media-img"
            light={active.topImg}
            dark={active.topImgDark}
            imgKey={active.topImg}
          >
            {active.badge && (
              <span className={"mtariffs__badge mtariffs__badge--" + active.badge.kind}>{active.badge.text}</span>
            )}
          </TopImageLayers>
        ) : (
          active.badge && (
            <span className={"mtariffs__badge mtariffs__badge--float mtariffs__badge--" + active.badge.kind}>
              {active.badge.text}
            </span>
          )
        )}

        <div className="mtariffs__body">
          <div className="mtariffs__head">
            <h3 className="mtariffs__title">{active.name}</h3>
            {active.subtitle && <p className="mtariffs__subtitle">{active.subtitle}</p>}
          </div>

          <ul className="mtariffs__features">
            {active.features.map((f, i) => (
              <FeatureRow key={f.key ?? i} f={f} flash={flashOf("f:" + f.key)} />
            ))}
          </ul>

          {/*
            Полоса indicator_line и блок connect («Встречайте…») не
            рисуются — как и в десктопной карточке. Поля остались в данных
            и в админке.
          */}
          <div className="mtariffs__footer">
            <div className="mtariffs__price-row">
              <span className={"mtariffs__price-main" + (flashOf("price") ? " is-flash" : "")}>{active.priceText}</span>
              {active.oldPriceText && <span className="mtariffs__price-old">{active.oldPriceText}</span>}
            </div>
            <button type="button" className="mtariffs__cta" onClick={() => onConnect && onConnect(active.raw)}>
              Подключить
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="mtariffs">
      <div
        className="mtariffs__viewport"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className={"mtariffs__track" + (dragging ? " is-dragging" : "")}
          style={{ transform: "translate3d(calc(" + (-index * 100) + "% + " + drag + "px), 0, 0)" }}
        >
          {tariffs.map(renderCard)}
        </div>
      </div>

      {tariffs.length > 1 && (
        <div className="mtariffs__dots">
          {tariffs.map((t, i) => (
            <button
              key={t.id ?? i}
              type="button"
              className={"mtariffs__dot" + (i === index ? " is-active" : "")}
              aria-label={"Тариф " + (i + 1)}
              aria-current={i === index}
              onClick={() => go(i)}
            />
          ))}
        </div>
      )}

    </div>
  );
}
